import { makeStyles } from "@material-ui/core/styles";
import React, {
  ChangeEvent,
  Children,
  cloneElement,
  isValidElement,
  ReactElement,
  ReactNode,
  useState,
} from "react";
import { Route, useRouteMatch, useLocation } from "react-router-dom";
import { Divider } from "@material-ui/core";
import {
  ClassesOverride,
  escapePath,
  getTabFullPath,
  TabbedFormTabs,
} from "react-admin";
import classnames from "classnames";
import { CompactFormToolbar } from "../CompactFormToolbar";
import { useFormContext } from "react-hook-form";
import { CompactSaveButton } from "../CompactSaveButton";

interface Props {
  children: ReactNode;
  classes?: ClassesOverride<typeof useTabbedFormViewStyles>;
  className?: string;
  tabs?: ReactElement;
  toolbar?: ReactElement;
  margin?: "none" | "normal" | "dense";
  variant?: "standard" | "outlined" | "filled";
}

export const useTabbedFormViewStyles = makeStyles(
  (theme) => ({
    errorTabButton: { color: theme.palette.error.main },
    content: {
      paddingTop: theme.spacing(1),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  }),
  { name: "RaTabbedForm" }
);

const DefaultCompactFormToolbar = () => {
  const {
    formState: { isSubmitting, isValid },
  } = useFormContext();
  return (
    <CompactFormToolbar>
      <CompactSaveButton
        saving={isSubmitting}
        disabled={isSubmitting || !isValid}
      />
    </CompactFormToolbar>
  );
};

export const HookFormTabbedForm = (props: Props) => {
  const {
    children,
    className,
    classes: classesOverride,
    tabs = <TabbedFormTabs />,
    toolbar = <DefaultCompactFormToolbar />,
    variant,
    margin,
    ...rest
  } = props;

  const classes = useTabbedFormViewStyles(props);
  const match = useRouteMatch();
  const location = useLocation();
  const url = match ? match.url : location.pathname;
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (_: ChangeEvent<{}>, value: any): void => {
    setTabValue(value);
  };

  return (
    <div className={classnames("tabbed-form", className)}>
      {cloneElement(
        tabs,
        {
          classes,
          url,
          onChange: handleTabChange,
          value: tabValue,
        },
        children
      )}
      <Divider />
      <div className={classes.content}>
        {Children.map(children, (tab, index) => {
          if (!tab) {
            return;
          }

          const tabPath = getTabFullPath(tab, index, url);
          return (
            <Route exact path={escapePath(tabPath)}>
              {(_) =>
                isValidElement<any>(tab)
                  ? React.cloneElement(tab, {
                      intent: "content",
                      classes,
                      hidden: tabValue !== index,
                      variant: tab.props.variant || variant,
                      margin: tab.props.margin || margin,
                      value: index,
                      ...sanitizeRestProps(rest),
                    })
                  : null
              }
            </Route>
          );
        })}
      </div>
      {toolbar}
    </div>
  );
};

const sanitizeRestProps = ({
  active,
  dirty,
  dirtyFields,
  dirtyFieldsSinceLastSubmit,
  dirtySinceLastSubmit,
  error,
  errors,
  form,
  hasSubmitErrors,
  hasValidationErrors,
  initialValues,
  modified = null,
  modifiedSinceLastSubmit,
  save = null,
  submitError,
  submitErrors,
  submitFailed,
  submitSucceeded,
  submitting,
  touched = null,
  valid,
  values,
  visited = null,
  __versions = null,
  ...props
}: any) => props;
